enum RequestType {
    GET = 'GET',
    POST = 'POST',
    PATCH = 'PATCH',
    DELETE = 'DELETE',
    PUT = 'PUT'
  }
  
  type MapHeader = Record<string, string>
  
  class APIRequest {
    type: RequestType = RequestType.GET
    fullUrl: string = ''
    body?: FormData | string = ''
    authToken?: string = ''
    extraHeaders?: MapHeader
    timeout?: number
  
    constructor(
      type: RequestType,
      fullUrl: string,
      body?: FormData | string,
      authToken?: string,
      extraHeaders?: MapHeader,
      timeout?: number,
    ) {
      this.type = type
      this.fullUrl = fullUrl
      this.body = body
      this.authToken = authToken
      this.extraHeaders = extraHeaders
      this.timeout = timeout ?? 30000
    }
  }
  
  interface Error {
    message: string
  }
  interface ResultType {
    errors?: Error[]
    error?: string
    data: string
    message?: string
  }
  
  class APIResponse {
    success: boolean = false
    statusCode: number = 0
    errorMessage?: string
    date?: string
    result: ResultType = {
      errors: [],
      error: '',
      data: '',
      message: ''
    }
  
    constructor(
      success: boolean,
      statusCode: number,
      result: ResultType,
      errorMessage?: string,
      date?: string,
    ) {
      this.success = success
      this.statusCode = statusCode
      this.result = result
      this.errorMessage = errorMessage
      this.date = date
    }
  }
  
  // Standard HTTP codes
  const HTTP_CODE_SUCCESS = 200
  const HTTP_CODE_SUCCESS1 = 201
  const HTTP_CODE_NOT_FOUND = 404
  // Custom HTTP codes to indicate errors
  const HTTP_CODE_BAD_RESPONSE = -1
  const HTTP_CODE_NO_INTERNET = -2
  const HTTP_CODE_UNKNOWN = -4
  
  type ErrorCodeName = Record<number, string>
  
  const errorCodeNames: ErrorCodeName = {
    [HTTP_CODE_NO_INTERNET]: 'No internet connection.',
    [HTTP_CODE_BAD_RESPONSE]: 'Something went wrong. Try again',
    [HTTP_CODE_NOT_FOUND]: 'Not found.',
    [HTTP_CODE_UNKNOWN]: 'Unknown error',
  }
  
  class HTTPClient {
    getHeaders(request: APIRequest) {
      const isAuthenticated = request.authToken !== undefined
  
      // Default headers
      const headers: MapHeader = {
        'Content-Type': 'application/json; charset=UTF-8',
      }
  
      /* eslint-disable */
      if (isAuthenticated) {
        headers.Authorization = `Bearer ${request.authToken}`
      }
  
      /* eslint-disable */
      if (request.extraHeaders !== undefined) {
        Object.keys(request.extraHeaders).forEach(
          (header) => (headers[header] = request.extraHeaders![header] ?? ''),
        )
      }
  
      return headers
    }
  
    async request(request: APIRequest) {
      try {
        const controller = new AbortController()
        const id = setTimeout(() => controller.abort(), request.timeout)
        
        let body;
        if (request.body instanceof FormData) {
            body = request.body;
        } else if (typeof request.body === 'string') {
            body = request.body;
        } else {
            body = JSON.stringify(request.body);
        }

        const headers = this.getHeaders(request)
        if(request.body instanceof FormData){
          delete headers["Content-Type"]
        }

        /* eslint-ignore */
        const response = await fetch(`${request.fullUrl}`, {
          method: request.type,
          headers,
          body: body ?? null,
          signal: controller.signal,
        })
  
        clearTimeout(id)
  
        const statusCode = response.status
  
        const data = await response.json()

        if(statusCode > 400) {
          return new APIResponse(
            false,
            statusCode,
            data,
            errorCodeNames[HTTP_CODE_BAD_RESPONSE],
            response.headers.get('date') ?? '',
          )
        }
  
        if (!(statusCode >= HTTP_CODE_SUCCESS && statusCode <= HTTP_CODE_SUCCESS1)) {
          return new APIResponse(
            false,
            statusCode,
            data,
            errorCodeNames[HTTP_CODE_BAD_RESPONSE],
            response.headers.get('date') ?? '',
          )
        }
  
        return new APIResponse(true, statusCode, data, '', response.headers.get('date') ?? '')
      } catch (_) {

        return new APIResponse(
          false,
          HTTP_CODE_BAD_RESPONSE,
          {
            data: '',
            error: errorCodeNames[HTTP_CODE_BAD_RESPONSE],
          },
          errorCodeNames[HTTP_CODE_BAD_RESPONSE],
        )
      }
    }
  
    async get(request: APIRequest) {
      return await this.request(request)
    }
  
    async post(request: APIRequest) {
      return await this.request(request)
    }
  
    async patch(request: APIRequest) {
      return await this.request(request)
    }

    async put(request: APIRequest) {
      return await this.request(request)
    }
  
    async delete(request: APIRequest) {
      return await this.request(request)
    }
  }
  
  export { HTTPClient, APIRequest, APIResponse, RequestType }
  